import moment from 'moment';
import React, { useEffect, useState } from 'react';
import * as yup from 'yup';
import DatePicker from '../../../Components/Base/DatePicker/DatePicker';
import TextField from '../../../Components/Base/TextField/TextField';
import LinkButton from '../../../Components/Button/LinkButton';
import SubmitButton from '../../../Components/Button/SubmitButton';
import NumberField from '../../../Components/Form/components/NumberField';
import Select, {
	SelectValueProps,
} from '../../../Components/Form/components/Select';
import FormControl from '../../../Components/Form/FormControl';
import Loading from '../../../Components/Loading';
import { Theme } from '../../../Enums/Theme.enum';
import { useLazyPatientInformationQuery } from '../../../graphql/graphql';
import { useEffectAsync } from '../../../helpers';
import useTranslation from '../../../Hooks/useTranslation';
import ScrollContainer from '../../Base/Components/ScrollContainer';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @ts-ignore
import styles from './EPrescriptionPatientInformation.module.scss';
import Text from '../../../Components/Base/Text/Text';
import SelectField from '../../../Components/Form/components/SelectField';
import FormWrapper from '../../../Components/Form/FormWrapper';
import PhoneNumberField from '../../../Components/PhoneNumber/PhoneNumberField';
import { TreatmentFormModalContextKeys } from '../../../Components/Treatment/TreatmentFormModalContextKeys';
import { useModalContext } from '../../Modal/Services/ModalContext';
import InputField from '../../../Components/Form/components/InputField';

export interface EPrescriptionPatientInformationFormData {
	city?: string;
	contactAddress?: string;
	country?: string;
	dateOfBirth?: Date;
	descriptiveNumber?: number;
	district?: string;
	documentNumber?: number;
	evidenceNumber?: number;
	electronicMail?: string;
	firstName?: string;
	gender?: SelectValueProps;
	guideNumber?: number;
	insuranceCompany?: SelectValueProps;
	insuranceNumber?: number;
	insuranceType?: SelectValueProps;
	lastName?: string;
	nameOfPrison?: string;
	phoneNumber?: string;
	postCode?: number;
	street?: string;
	weight?: number;
	idType?: SelectValueProps;

	[key: string]: any;
}

interface EPrescriptionPatientInformationProps {
	patientId: string;
	onSuccess?: (values: EPrescriptionPatientInformationFormData) => void;
	formValues?: Record<string, any>;
}

const EPrescriptionPatientInformation: React.FC<
	EPrescriptionPatientInformationProps
> = ({ patientId, onSuccess, formValues }) => {
	const [isInitialized, setIsInitialized] = useState(false);
	const [defaultValues, setDefaultValues] =
		useState<EPrescriptionPatientInformationFormData>({});
	const [getPatientInformation] = useLazyPatientInformationQuery();
	const [isSelfPayer, setIsSelfPayer] = useState(false);
	const { registerFormByKey } = useModalContext();

	const { t } = useTranslation();
	const [isOpened, setIsOpened] = useState({
		column1: false,
		column2: false,
	});

	const insuranceTypeDefaults: any = {
		['Veřejné zdravotní pojištění']: {
			label: t('terms.suklDruhPojisteni.verejne'),
			value: 'VEREJNE',
		},
		['1 - veřejné zdravotní pojištění (nepřeúčtované přes CMU)']: {
			label: t('terms.suklDruhPojisteni.verejne'),
			value: 'VEREJNE',
		},
		['Smluvní pojištění']: {
			label: t('terms.suklDruhPojisteni.smluvni'),
			value: 'SMLUVNI_PRIPOJISTENI',
		},
		['Cestovní pojištění']: {
			label: t('terms.suklDruhPojisteni.cestovni'),
			value: 'CESTOVNI_PRIPOJISTENI',
		},
		['Mezistátní pojištění (včetně EU)']: {
			label: t('terms.suklDruhPojisteni.pojisteniEu'),
			value: 'POJISTENI_EU',
		},
	};

	const handleSave = async (
		values: EPrescriptionPatientInformationFormData,
	) => {
		onSuccess?.(values);
	};

	const insuranceCompanies = [
		{ label: '213 - RBP', value: '213' },
		{ label: '201 - VoZP', value: '201' },
		{ label: '111 - VZP', value: '111' },
		{ label: '209 - ZPŠ', value: '209' },
		{ label: '211 - ZPMV', value: '211' },
		{ label: '205 - ČPZP', value: '205' },
		{ label: '207 - OZP', value: '207' },
		{ label: '999 - Samoplátce', value: '999' },
	];

	const insuranceTypes = [
		{ label: 'terms.suklDruhPojisteni.verejne', value: 'VEREJNE' },
		{
			label: 'terms.suklDruhPojisteni.smluvni',
			value: 'SMLUVNI_PRIPOJISTENI',
		},
		{
			label: 'terms.suklDruhPojisteni.cestovni',
			value: 'CESTOVNI_PRIPOJISTENI',
		},
		{
			label: 'terms.suklDruhPojisteni.pojisteniEu',
			value: 'POJISTENI_EU',
		},
	];

	const genders = [
		{ label: 'base.man', value: 'muz' },
		{ label: 'base.woman', value: 'zena' },
	];

	const identificationTypes = [
		{ label: 'base.passport', value: 'pas' },
		{ label: 'base.idCard', value: 'obcanka' },
	];

	const validationSchema = yup.object({
		firstName: yup.string().required(t('base.shared.firstName')),
		lastName: yup.string().required(t('base.shared.lastName')),
		city: yup.string(),
		contactAddress: yup.string(),
		county: yup.string(),
		dateOfBirth: yup
			.date()
			.notRequired()
			.typeError('validation.ePrescription.date')
			.nullable()
			.transform(value => (!!value ? value : null)),
		descriptiveNumber: yup
			.number()
			.notRequired()
			.nullable()
			.transform(value => (!!value ? value : null))
			.typeError('validation.ePrescription.number'),
		district: yup.string(),
		documentNumber: yup
			.string()
			.notRequired()
			.min(1)
			.max(9)
			.nullable()
			.transform(value => (!!value ? value : null)),
		electronicMail: yup.string().email(),
		evidenceNumber: yup
			.number()
			.notRequired()
			.nullable()
			.transform(value => (!!value ? value : null))
			.typeError('validation.ePrescription.number'),
		gender: yup
			.object({
				label: yup.string(),
				value: yup.string(),
			})
			.nullable(),
		guideNumber: yup
			.number()
			.notRequired()
			.nullable()
			.transform(value => (!!value ? value : null))
			.typeError('validation.ePrescription.number'),
		insuranceCompany: yup
			.object({
				label: yup.string(),
				value: yup.string(),
			})
			.required('')
			.typeError('validation.ePrescription.insuranceCompany'),
		insuranceNumber: yup
			.string()
			.notRequired()
			.nullable()
			.transform(value => (!!value ? value : null))
			.typeError('validation.ePrescription.number'),
		insuranceType: yup
			.object({
				label: yup.string(),
				value: yup.string(),
			})
			.nullable()
			.typeError('validation.ePrescription.insuranceType'),
		nameOfPrison: yup
			.string()
			.nullable()
			.max(200, 'base.error.prisonNameFormat'),
		phoneNumber: yup
			.string()
			.notRequired()
			.nullable()
			.transform(value => (!!value ? value : null))
			.typeError('validation.ePrescription.number'),
		postCode: yup
			.string()
			.notRequired()
			.length(5, 'base.shared.form.postCode.validationErrorMessage')
			.nullable()
			.transform(value => (!!value ? value : null)),
		street: yup.string(),
		weight: yup
			.number()
			.notRequired()
			.nullable()
			.transform(value => (!!value ? value : null))
			.typeError('validation.ePrescription.number'),
		idType: yup
			.object({
				label: yup.string(),
				value: yup.string(),
			})
			.nullable()
			.typeError('validation.ePrescription.idType'),
	});

	const formMethods = useForm({
		defaultValues: { ...defaultValues, ...formValues } || {},
		resolver: yupResolver(validationSchema),
	});

	useEffectAsync(async () => {
		const { user } = await getPatientInformation({
			userId: patientId,
		}).unwrap();

		if (user && user.profile) {
			const {
				firstName,
				lastName,
				birthDate,
				address,
				insuranceNumber,
				insuranceCompanyTermItem,
				gender,
			} = user.profile;

			const newDefaultValues = {
				...defaultValues,
				firstName,
				lastName,
				dateOfBirth: birthDate ? moment(birthDate) : null,
				phoneNumber: user?.formattedPhoneNumber ?? undefined,
				electronicMail: user?.email ?? undefined,
				insuranceNumber,
				insuranceType:
					insuranceTypeDefaults[
						user?.vzpResult?.result?.prubehPojisteni?.druhPojisteni
					],
				insuranceCompany: insuranceCompanyTermItem
					? {
							label: `${insuranceCompanyTermItem.value} - ${insuranceCompanyTermItem.value2}`,
							value: insuranceCompanyTermItem.value,
					  }
					: null,
				gender: gender ?? undefined,
				...(address && {
					street: address.street,
					descriptiveNumber: address.descriptiveNumber,
					guideNumber: address.guideNumber,
					city: address.city,
					postCode: address.postCode,
				}),
				...(user?.healthCard && { weight: user?.healthCard.weight }),
			};
			setDefaultValues(newDefaultValues as any);
			formMethods.reset(newDefaultValues as any);
		}

		setIsInitialized(true);
	}, []);

	useEffect(() => {
		if (formMethods && isInitialized) {
			registerFormByKey(
				TreatmentFormModalContextKeys.PRESCRIPTION_STEP_1,
				() => formMethods,
			);
		}
	}, [formMethods, isInitialized]);

	const selectedInsuranceCompany = formMethods.watch('insuranceCompany');

	useEffect(() => {
		if (selectedInsuranceCompany?.value === '999') {
			setIsSelfPayer(true);
		}
	}, [selectedInsuranceCompany]);

	if (!isInitialized) return <Loading />;

	return (
		<ScrollContainer className={styles.wrapper}>
			<FormWrapper formMethods={formMethods}>
				<Text tx={'base.shared.patient'} className={styles.title} firstUpper />
				<div className='row'>
					<div className='col-md-6'>
						<FormControl
							label='base.shared.firstName'
							name='firstName'
							isRequired>
							<TextField />
						</FormControl>
						<FormControl
							label='base.shared.lastName'
							name='lastName'
							isRequired>
							<TextField />
						</FormControl>
						<FormControl
							label='base.shared.dateOfBirth'
							name='dateOfBirth'
							isRequired>
							<DatePicker maxDate={moment()} />
						</FormControl>
						<InputField
							label='base.shared.insuranceNumber'
							name='insuranceNumber'
							isRequired
						/>
						<FormControl
							label='base.shared.insuranceCompany'
							name='insuranceCompany'
							isRequired>
							<SelectField
								name='insuranceCompany'
								options={insuranceCompanies}
							/>
						</FormControl>
						{!isSelfPayer && (
							<FormControl
								label='base.typeOfInsurance'
								name='insuranceType'
								isRequired>
								<SelectField name='insuranceType' options={insuranceTypes} />
							</FormControl>
						)}
						<PhoneNumberField
							name='phoneNumber'
							label='base.shared.phone'
							isRequired
						/>
						{isOpened.column1 && (
							<>
								<FormControl
									label='base.shared.gender'
									name='gender'
									isRequired>
									<SelectField name='gender' options={genders} />
								</FormControl>
								<FormControl
									label='base.documentNumber'
									name='documentNumber'
									isRequired>
									<TextField />
								</FormControl>
								<FormControl label='base.shared.type' name='idType' isRequired>
									<SelectField name='idType' options={identificationTypes} />
								</FormControl>
								<NumberField label='base.weight' name='weight' isRequired />
								<FormControl
									label='base.electronicMail'
									name='electronicMail'
									isRequired>
									<TextField />
								</FormControl>
							</>
						)}
						<LinkButton
							onClick={() =>
								setIsOpened(({ column1, column2 }) => ({
									column1: !column1,
									column2,
								}))
							}
							theme={Theme.BLUE}
							title={isOpened.column1 ? 'base.showLess2' : 'base.showMore'}
						/>
					</div>
					<div className='col-md-6'>
						<FormControl label='base.shared.street' name='street' isRequired>
							<TextField />
						</FormControl>
						<NumberField
							label='base.shared.descriptiveNumber'
							name='descriptiveNumber'
							isRequired
						/>
						{isOpened.column2 && (
							<>
								<NumberField
									label='base.shared.guideNumber'
									name='guideNumber'
									isRequired
								/>
								<NumberField
									label='base.registryNumber'
									name='evidenceNumber'
									isRequired
								/>
							</>
						)}
						<FormControl
							label='component.ePrescription.village'
							name='city'
							isRequired>
							<TextField />
						</FormControl>
						<NumberField
							label='base.shared.postCode'
							name='postCode'
							isRequired
						/>
						<FormControl
							label='component.ePrescription.contactAddress'
							name='contactAddress'
							isRequired>
							<TextField />
						</FormControl>
						{isOpened.column2 && (
							<>
								<FormControl
									label={'component.ePrescription.district'}
									name='county'
									isRequired>
									<TextField />
								</FormControl>
								<FormControl
									label={'component.ePrescription.partOfVillage'}
									name='district'
									isRequired>
									<TextField />
								</FormControl>
								<FormControl
									label={'component.ePrescription.nameOfPrison'}
									name='nameOfPrison'
									isRequired>
									<TextField />
								</FormControl>
							</>
						)}
						<LinkButton
							onClick={() =>
								setIsOpened(({ column1, column2 }) => ({
									column1,
									column2: !column2,
								}))
							}
							theme={Theme.BLUE}
							title={isOpened.column2 ? 'base.showLess2' : 'base.showMore'}
						/>
					</div>
				</div>
				<div className='d-flex mt-4 justify-content-between'>
					<SubmitButton title='base.shared.continue' onSubmit={handleSave} />
				</div>
			</FormWrapper>
		</ScrollContainer>
	);
};

export default EPrescriptionPatientInformation;
